import React from 'react';
import {
  SegmentedControl, Select, TextInput, Textarea, useToast,
} from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { gql } from '../../lib/api';
import TaskDialog from '../../components/TaskDialog';
import { CALENDARS, CLINIC_SWITCHES } from '../../constants/countryProfile';
import { SET_CLINIC_COUNTRY_SETTINGS } from '../Countries/queries';
import { NotReadBadge, countryErrorMessage } from '../Countries/shared';
import { NOTE_MAX, NOTE_MIN } from '../Countries/EditGroupDialog';

/** clinicmanagement's shape for a وزارت بهداشت id (`[A-Za-z0-9._-]`, at most 64). */
const MOHME_ID = /^[A-Za-z0-9._-]{1,64}$/;

/**
 * A clinic's own settings inside its country (PM-0218): three switches that
 * can only TAKE AWAY, its وزارت بهداشت ids, and a calendar nothing reads yet.
 *
 * A switch offers «پیرو کشور» or «خاموش برای این کلینیک» — never «روشن»,
 * because an override cannot add what the country does not allow. A stored
 * `true` (follow as well) is shown as «پیرو کشور» and sent back as it was
 * unless the operator changes it. Only the fields that moved are sent; the BFF
 * re-reads the stored settings and refuses a stale form (CONFLICT).
 */
export default function CountrySettingsDialog({
  open, onOpenChange, clinicId, settings, countryLabel, market, onSaved,
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const initial = React.useMemo(() => ({
    erxEnabled: settings?.erxEnabled ?? null,
    smsEnabled: settings?.smsEnabled ?? null,
    insuranceEnabled: settings?.insuranceEnabled ?? null,
    mohmeOrgId: settings?.mohmeOrgId || '',
    universityId: settings?.universityId || '',
    calendar: settings?.calendar || '',
  }), [settings]);

  const [draft, setDraft] = React.useState(initial);
  const [note, setNote] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setDraft(initial);
    setNote('');
    setError(null);
  }, [open, initial]);

  // Outside the Iranian market the e-prescription switch and the وزارت بهداشت ids
  // are not offered — and, not being in `changes`, keep whatever is stored.
  const intl = market === 'intl';
  const switches = CLINIC_SWITCHES.filter(({ field }) => !(intl && field === 'erxEnabled'));
  const ids = intl ? [] : ['mohmeOrgId', 'universityId'];

  const changes = {};
  for (const key of Object.keys(initial)) {
    const next = typeof draft[key] === 'string' ? draft[key].trim() : draft[key];
    const was = initial[key];
    if (next !== was) changes[key] = next === '' ? null : next;
  }
  const changed = Object.keys(changes).length > 0;
  const idError = (v) => (v.trim() && !MOHME_ID.test(v.trim()) ? t('countries.clinicCard.invalidId') : null);
  const invalid = !!(idError(draft.mohmeOrgId) || idError(draft.universityId));
  const trimmed = note.trim();
  const noteOk = trimmed.length >= NOTE_MIN && trimmed.length <= NOTE_MAX;

  const submit = async () => {
    if (!changed || invalid || !noteOk || pending) return;
    setPending(true);
    setError(null);
    try {
      const res = await gql(SET_CLINIC_COUNTRY_SETTINGS, {
        clinicId, changes, note: trimmed, expectedUpdatedAt: settings?.updatedAt ?? null,
      });
      onOpenChange(false);
      toast.success(t('countries.clinicCard.success'));
      onSaved?.(res?.setClinicCountrySettings);
    } catch (e) {
      setError(countryErrorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  return (
    <TaskDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('countries.clinicCard.dialogTitle')}
      error={error}
      pending={pending}
      dirty={changed || !!note}
      submitLabel={t('countries.edit.submit')}
      submitDisabled={!changed || invalid || !noteOk}
      onSubmit={submit}
      footerNote={!changed ? t('countries.edit.noChanges') : (!noteOk ? t('countries.edit.noteNeeded') : undefined)}
      data-testid="clinic-country-edit"
    >
      <p className="text-sm leading-6 text-muted-foreground">
        {t('countries.clinicCard.dialogBody', { country: countryLabel })}
      </p>

      {switches.map(({ field }) => {
        const off = draft[field] === false;
        return (
          <div key={field} className="flex flex-col gap-1.5" data-testid={`clinic-country-edit-${field}`}>
            <span className="text-[11.5px] font-semibold text-muted-foreground">{t(`countries.clinicCard.switches.${field}`)}</span>
            <SegmentedControl
              semantics="filter"
              size="sm"
              ariaLabel={t(`countries.clinicCard.switches.${field}`)}
              options={[
                { value: 'follow', label: t('countries.clinicCard.follow') },
                { value: 'off', label: t('countries.clinicCard.off') },
              ]}
              value={off ? 'off' : 'follow'}
              // Back to «پیرو کشور» restores what was stored (null, or a stored true).
              onChange={(m) => setDraft((d) => ({
                ...d, [field]: m === 'off' ? false : (initial[field] === false ? null : initial[field]),
              }))}
            />
            {off ? (
              // What the choice does, not an error: warning ink, never red.
              <span className="text-[11.5px] text-[var(--chip-warning-ink)]">{t(`countries.clinicCard.offMeans.${field}`)}</span>
            ) : null}
          </div>
        );
      })}

      {ids.map((field) => (
        <div key={field} data-testid={`clinic-country-edit-${field}`}>
          <TextInput
            label={t(`countries.clinicCard.ids.${field}`)}
            value={draft[field]}
            onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target?.value ?? e }))}
            dir="ltr"
            maxLength={64}
            helperText={idError(draft[field]) ? undefined : t('countries.clinicCard.ids.emptyMeans')}
            error={idError(draft[field]) || undefined}
            disabled={pending}
          />
        </div>
      ))}

      <div data-testid="clinic-country-edit-calendar">
        <Select
          label={<>{t('countries.clinicCard.calendar')}<NotReadBadge field="calendar" /></>}
          value={draft.calendar}
          onChange={(e) => setDraft((d) => ({ ...d, calendar: e.target?.value ?? e }))}
          options={[
            { value: '', label: t('countries.clinicCard.follow') },
            ...CALENDARS.map((c) => ({ value: c, label: t(`countries.values.calendar.${c}`) })),
          ]}
          placeholder={null}
          disabled={pending}
        />
      </div>

      <div data-testid="clinic-country-edit-note">
        <Textarea
          label={t('countries.edit.note')}
          required
          value={note}
          onChange={(e) => setNote(e.target?.value ?? e)}
          placeholder={t('countries.clinicCard.notePlaceholder')}
          helperText={t('countries.clinicCard.noteHelper')}
          maxLength={NOTE_MAX}
          showCount
          rows={2}
          disabled={pending}
        />
      </div>
    </TaskDialog>
  );
}
